/*
 * stone-filter-data.js — Custom Stone Filter data layer (Phase 2 unified)
 *
 * Architecture:
 *   • One snapshot file (assets/all-stones-snapshot.json) covers every
 *     product in the broader Kloc collection (~11.5K).
 *   • Each shape-specific template sets locked_shape="<Round|Oval|...>" which
 *     the UI pre-applies as a baseShape filter; the unified template leaves
 *     it empty and renders a Shape filter group.
 *   • Snapshot fields use full names (baseShape, specialty, carat, ...).
 *
 * Exposed:
 *   window.StoneFilter.data — classifyShape, loadAll, backgroundRefresh,
 *                              applyFilters, facetCounts, STANDARD_SHAPES
 *   window.StoneFilter.api  — buildFilterURL, parseFilterURL
 */
(function (root) {
  'use strict';

  const SF = root.StoneFilter = root.StoneFilter || {};

  // ─── Config ────────────────────────────────────────────────────
  const DB_NAME = 'stone-filter';
  const DB_VERSION = 1;
  const STORE_NAME = 'collections';
  const API_VERSION = '2024-10';
  const PAGE_SIZE = 250;

  // ─── Shape taxonomy + classifier (per Phase 2 spec) ───────────
  const STANDARD_SHAPES = ['Round','Oval','Princess','Cushion','Emerald','Radiant','Asscher','Marquise','Pear','Heart'];

  function classifyShape(rawShape) {
    if (rawShape == null) return { baseShape: null, specialty: null };
    const value = String(rawShape).trim();
    if (!value) return { baseShape: null, specialty: null };
    for (const std of STANDARD_SHAPES) {
      if (value.toLowerCase() === std.toLowerCase()) {
        return { baseShape: std, specialty: null };
      }
    }
    for (const std of STANDARD_SHAPES) {
      const regex = new RegExp(`\\b${std}\\b`, 'i');
      if (regex.test(value)) {
        const remainder = value.replace(regex, '').trim().replace(/\s+/g, ' ');
        return { baseShape: std, specialty: remainder || null };
      }
    }
    return { baseShape: null, specialty: value };
  }

  // ─── IndexedDB cache ───────────────────────────────────────────
  function openDB() {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  async function cacheGet(key) {
    try {
      const db = await openDB();
      return await new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const req = tx.objectStore(STORE_NAME).get(key);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => resolve(null);
      });
    } catch { return null; }
  }
  async function cachePut(key, value) {
    try {
      const db = await openDB();
      return await new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(value, key);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
      });
    } catch { return false; }
  }

  // ─── Storefront API client ─────────────────────────────────────
  async function sfFetch(ctx, query, variables) {
    const url = `https://${ctx.shopDomain}/api/${API_VERSION}/graphql.json`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': ctx.storefrontToken,
      },
      body: JSON.stringify({ query, variables }),
    });
    const j = await res.json();
    if (j.errors) throw new Error('Storefront API error: ' + JSON.stringify(j.errors));
    return j.data;
  }

  // Mirrors scripts/gha-sync.mjs#parseMeasurements — keep in sync.
  function parseMeasurements(raw) {
    if (raw == null) return { lengthMm: null, widthMm: null };
    const v = String(raw).trim();
    if (!v) return { lengthMm: null, widthMm: null };
    const parts = v.split(/[x×]/i).map(s => s.trim()).filter(Boolean);
    if (parts.length < 2) return { lengthMm: null, widthMm: null };
    const firstHasRange = /-/.test(parts[0]);
    if (firstHasRange) {
      const [lo, hi] = parts[0].split('-').map(s => parseFloat(s));
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { lengthMm: null, widthMm: null };
      const mid = (lo + hi) / 2;
      return { lengthMm: mid, widthMm: mid };
    }
    const a = parseFloat(parts[0]);
    const b = parseFloat(parts[1]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return { lengthMm: null, widthMm: null };
    return { lengthMm: Math.max(a, b), widthMm: Math.min(a, b) };
  }

  // ─── Normalize product (from live Storefront API fallback path) ──
  function normalizeFromLive(node) {
    const mfMap = Object.create(null);
    for (const m of (node.metafields || [])) if (m) mfMap[m.key] = m.value;
    const { baseShape, specialty } = classifyShape(mfMap.shape);
    const carat = parseFloat(mfMap.weight);
    const { lengthMm, widthMm } = parseMeasurements(mfMap.measurements);
    return {
      id: node.handle,
      handle: node.handle,
      title: node.title,
      available: !!node.availableForSale,
      price: parseFloat(node.priceRange?.minVariantPrice?.amount) || null,
      image: node.featuredImage?.url || null,
      baseShape,
      specialty,
      carat: Number.isFinite(carat) ? carat : null,
      caratBucket: mfMap.caratsize || null,
      lengthMm,
      widthMm,
      color: mfMap.color || null,
      clarity: mfMap.clarity || null,
      cutGrade: mfMap.cut || null,
    };
  }

  // ─── Expand snapshot record (already full-key, just defensive copy) ──
  function expandSnapshotRecord(r) {
    return {
      id: r.id || r.handle,
      handle: r.handle,
      title: r.title,
      available: !!r.available,
      price: Number.isFinite(r.price) ? r.price : null,
      image: r.image,
      baseShape: r.baseShape,
      specialty: r.specialty,
      carat: Number.isFinite(r.carat) ? r.carat : null,
      caratBucket: r.caratBucket,
      lengthMm: Number.isFinite(r.lengthMm) ? r.lengthMm : null,
      widthMm:  Number.isFinite(r.widthMm)  ? r.widthMm  : null,
      color: r.color,
      clarity: r.clarity,
      cutGrade: r.cutGrade,
    };
  }

  async function fetchAsset(url) {
    if (!url) return null;
    try {
      // The URL is content-fingerprinted by Shopify (asset_url appends ?v=<hash>):
      // a changed snapshot ⇒ a new URL ⇒ fresh fetch; unchanged ⇒ served from the
      // browser HTTP cache. force-cache avoids needless revalidation round-trips.
      // (We deliberately do NOT fetch the unfingerprinted URL — Shopify's CDN pins
      // it to a long-lived edge object that doesn't reliably invalidate.)
      const r = await fetch(url, { cache: 'force-cache' });
      if (!r.ok) return null;
      return await r.json();
    } catch (e) {
      console.warn('[stone-filter] asset fetch failed', url, e);
      return null;
    }
  }

  // ─── Live fallback: paginate the source collection via Storefront API ──
  const COLLECTION_QUERY = `
    query GetCollectionProducts($handle: String!, $cursor: String) {
      collection(handle: $handle) {
        handle title
        products(first: ${PAGE_SIZE}, after: $cursor) {
          pageInfo { hasNextPage endCursor }
          edges { node {
            id handle title availableForSale
            priceRange { minVariantPrice { amount } }
            featuredImage { url(transform: {maxWidth: 400}) }
            metafields(identifiers: [
              { namespace: "custom", key: "shape" },
              { namespace: "custom", key: "weight" },
              { namespace: "custom", key: "color" },
              { namespace: "custom", key: "clarity" },
              { namespace: "custom", key: "cut" },
              { namespace: "custom", key: "caratsize" }
            ]) { key value }
          } }
        }
      }
    }`;

  async function fetchCollectionLive(ctx, handle) {
    let cursor = null;
    const out = [];
    while (true) {
      const data = await sfFetch(ctx, COLLECTION_QUERY, { handle, cursor });
      if (!data?.collection) throw new Error('Collection not found: ' + handle);
      const conn = data.collection.products;
      for (const e of conn.edges) out.push(normalizeFromLive(e.node));
      if (!conn.pageInfo.hasNextPage) break;
      cursor = conn.pageInfo.endCursor;
    }
    return out;
  }

  // ─── Main load function ────────────────────────────────────────
  async function loadAll(ctx, opts) {
    opts = opts || {};
    const cacheKey = `all-stones::${ctx.cacheVersion || 'v3'}`;

    console.log('[stone-filter] loadAll start', { cacheKey, ctx: {
      collectionHandle: ctx.collectionHandle,
      snapshotUrl: ctx.snapshotUrl,
      sourceCollectionHandle: ctx.sourceCollectionHandle,
      lockedShape: ctx.lockedShape,
      cacheVersion: ctx.cacheVersion,
    }});

    let products = null;
    let source = null;

    // 1. Snapshot is the source of truth, re-read on every load (no TTL gate).
    //    Because the URL is content-fingerprinted, a returning visitor whose
    //    page HTML is current picks up each daily snapshot update automatically —
    //    no cache_version bump needed. (Previously a 24h IndexedDB TTL shadowed
    //    updates for up to a day even when the fresh snapshot was available.)
    if (ctx.snapshotUrl) {
      const snap = await fetchAsset(ctx.snapshotUrl);
      if (snap?.records?.length) {
        products = snap.records.map(expandSnapshotRecord);
        source = 'snapshot';
      } else {
        console.warn('[stone-filter] Snapshot asset empty or unreachable', ctx.snapshotUrl);
      }
    }

    // 2. Offline / network-failure fallback: reuse the last IndexedDB copy
    //    regardless of age (stale is better than empty when truly offline).
    if (!products) {
      const cached = await cacheGet(cacheKey);
      if (cached?.products?.length) {
        console.warn('[stone-filter] Snapshot unreachable → using IndexedDB fallback', { count: cached.products.length, fetchedAt: new Date(cached.fetchedAt).toISOString() });
        return { ...cached, fromCache: true };
      }
    }

    // 3. Last resort: live Storefront API scan.
    if (!products) {
      const fallbackHandle = ctx.sourceCollectionHandle || ctx.collectionHandle;
      console.warn(`[stone-filter] Falling back to live Storefront API scan of ${fallbackHandle}`);
      products = await fetchCollectionLive(ctx, fallbackHandle);
      source = 'live';
    }

    // Debug summary
    const baseHist = {};
    let specialtyCount = 0;
    for (const p of products) {
      const k = p.baseShape || '(null)';
      baseHist[k] = (baseHist[k] || 0) + 1;
      if (p.specialty) specialtyCount++;
    }
    console.log(`[stone-filter] Loaded ${products.length} products [source: ${source}]`);
    console.log('[stone-filter] baseShape distribution:', baseHist);
    console.log(`[stone-filter] Products with specialty (non-null): ${specialtyCount}`);

    const payload = {
      products,
      source,
      count: products.length,
      fetchedAt: Date.now(),
    };
    await cachePut(cacheKey, payload);
    return { ...payload, fromCache: false };
  }

  // ─── Background refresh ────────────────────────────────────────
  // Re-checks availability + price for products in the URL's collection
  // (i.e. for a locked-shape page, only that shape; for the unified page,
  // the broader source). Partial refresh — does not pick up newly-added
  // products from outside the current scope; that requires snapshot rebuild.
  async function backgroundRefresh(ctx, currentProducts) {
    const handle = ctx.collectionHandle;
    if (!handle) return { changes: 0 };
    let cursor = null;
    const live = new Map();
    while (true) {
      const data = await sfFetch(ctx, `
        query($handle:String!, $cursor:String){
          collection(handle:$handle){
            products(first:250, after:$cursor){
              pageInfo{ hasNextPage endCursor }
              edges{ node{
                handle availableForSale
                priceRange{ minVariantPrice{ amount } }
              } }
            }
          }
        }`, { handle, cursor });
      if (!data?.collection) break;
      const conn = data.collection.products;
      for (const e of conn.edges) {
        const n = e.node;
        live.set(n.handle, {
          available: n.availableForSale,
          price: parseFloat(n.priceRange?.minVariantPrice?.amount),
        });
      }
      if (!conn.pageInfo.hasNextPage) break;
      cursor = conn.pageInfo.endCursor;
    }
    const updates = [];
    for (const p of currentProducts) {
      const live_ = live.get(p.handle);
      if (!live_) continue;
      const next = {};
      if (live_.available !== p.available) next.available = live_.available;
      if (Number.isFinite(live_.price) && Math.abs((live_.price || 0) - (p.price || 0)) > 0.005) next.price = live_.price;
      if (Object.keys(next).length) updates.push({ handle: p.handle, ...next });
    }
    return { changes: updates.length, updates };
  }

  // ─── Filter application ────────────────────────────────────────
  // Tolerance applied to user-entered mm values (DovEggs business rule):
  // a user typing "9" should match a 8.7..9.3 mm stone.
  const MM_TOLERANCE = 0.3;

  function applyFilters(products, state) {
    state = state || {};
    const lc = (arr) => (arr || []).map(s => String(s).toLowerCase());
    const baseShapeSel = state.baseShape ? String(state.baseShape).toLowerCase() : null;
    const colorSel    = lc(state.color);
    const claritySel  = lc(state.clarity);
    const cutSel      = lc(state.cutGrade);
    const specSel     = lc(state.specialty);

    // Expand user mm window by ±MM_TOLERANCE.
    const lMin = Number.isFinite(state.lengthMin) ? state.lengthMin - MM_TOLERANCE : null;
    const lMax = Number.isFinite(state.lengthMax) ? state.lengthMax + MM_TOLERANCE : null;
    const wMin = Number.isFinite(state.widthMin)  ? state.widthMin  - MM_TOLERANCE : null;
    const wMax = Number.isFinite(state.widthMax)  ? state.widthMax  + MM_TOLERANCE : null;

    return products.filter(p => {
      if (state.availability === 'in-stock' && !p.available) return false;
      if (state.availability === 'out-of-stock' && p.available) return false;
      if (state.priceMin != null && p.price < state.priceMin) return false;
      if (state.priceMax != null && p.price > state.priceMax) return false;
      if (state.caratMin != null && (p.carat == null || p.carat < state.caratMin)) return false;
      if (state.caratMax != null && (p.carat == null || p.carat > state.caratMax)) return false;
      if (lMin != null && (p.lengthMm == null || p.lengthMm < lMin)) return false;
      if (lMax != null && (p.lengthMm == null || p.lengthMm > lMax)) return false;
      if (wMin != null && (p.widthMm  == null || p.widthMm  < wMin)) return false;
      if (wMax != null && (p.widthMm  == null || p.widthMm  > wMax)) return false;
      if (baseShapeSel && (!p.baseShape || String(p.baseShape).toLowerCase() !== baseShapeSel)) return false;
      if (colorSel.length   && (!p.color   || !colorSel.includes(String(p.color).toLowerCase())))     return false;
      if (claritySel.length && (!p.clarity || !claritySel.includes(String(p.clarity).toLowerCase()))) return false;
      if (cutSel.length     && (!p.cutGrade || !cutSel.includes(String(p.cutGrade).toLowerCase())))   return false;
      if (specSel.length) {
        const cv = p.specialty ? String(p.specialty).toLowerCase() : null;
        if (!cv || !specSel.includes(cv)) return false;
      }
      return true;
    });
  }

  // ─── URL serialization ─────────────────────────────────────────
  // URL schema (Stylist contract — DO NOT rename params without coordinating):
  //   ?shape=round&color=d,e,f&clarity=vvs1,vvs2&cut=ex,vg
  //   &carat=1.0-2.0&price=500-3000&availability=in-stock
  //   &specialty=rose-cut,old-european-cut&sort=price-asc
  function slug(s) { return String(s).toLowerCase().trim().replace(/\s+/g, '-'); }
  function unslug(s) { return String(s).toLowerCase().trim().replace(/-/g, ' '); }

  function buildFilterURL(basePath, state) {
    const params = new URLSearchParams();
    if (state.baseShape) params.set('shape', slug(state.baseShape));
    if (state.color?.length) params.set('color', state.color.map(slug).join(','));
    if (state.clarity?.length) params.set('clarity', state.clarity.map(slug).join(','));
    if (state.cutGrade?.length) params.set('cut', state.cutGrade.map(slug).join(','));
    if (state.specialty?.length) params.set('specialty', state.specialty.map(slug).join(','));
    if (state.caratMin != null || state.caratMax != null) {
      params.set('carat', `${state.caratMin ?? ''}-${state.caratMax ?? ''}`);
    }
    if (state.priceMin != null || state.priceMax != null) {
      params.set('price', `${state.priceMin ?? ''}-${state.priceMax ?? ''}`);
    }
    if (state.lengthMin != null || state.lengthMax != null) {
      params.set('length', `${state.lengthMin ?? ''}-${state.lengthMax ?? ''}`);
    }
    if (state.widthMin != null || state.widthMax != null) {
      params.set('width', `${state.widthMin ?? ''}-${state.widthMax ?? ''}`);
    }
    if (state.availability) params.set('availability', slug(state.availability));
    if (state.sort) params.set('sort', slug(state.sort));
    if (Number.isFinite(state.page) && state.page > 1) params.set('page', String(state.page));
    const qs = params.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  }

  function parseFilterURL(url) {
    const u = new URL(url, root.location?.origin || 'https://example.com');
    const p = u.searchParams;
    const csv = (k) => (p.get(k) || '').split(',').map(s => s.trim()).filter(Boolean).map(unslug);
    const range = (k) => {
      const raw = p.get(k);
      if (!raw) return [null, null];
      const [a, b] = raw.split('-');
      const min = a === '' || a == null ? null : parseFloat(a);
      const max = b === '' || b == null ? null : parseFloat(b);
      return [Number.isFinite(min) ? min : null, Number.isFinite(max) ? max : null];
    };
    const [caratMin, caratMax] = range('carat');
    const [priceMin, priceMax] = range('price');
    const [lengthMin, lengthMax] = range('length');
    const [widthMin, widthMax]   = range('width');
    const rawShape = p.get('shape');
    const rawPage = parseInt(p.get('page'), 10);
    return {
      baseShape: rawShape ? unslug(rawShape).replace(/^./, c => c.toUpperCase()) : null,
      color: csv('color'),
      clarity: csv('clarity'),
      cutGrade: csv('cut'),
      specialty: csv('specialty'),
      caratMin, caratMax,
      priceMin, priceMax,
      lengthMin, lengthMax,
      widthMin, widthMax,
      availability: p.get('availability') || null,
      sort: p.get('sort') || null,
      page: Number.isFinite(rawPage) && rawPage > 1 ? rawPage : 1,
    };
  }

  // ─── Facet counts ──────────────────────────────────────────────
  function facetCounts(products, state, dimension) {
    const stripped = { ...state, [dimension]: null };
    const subset = applyFilters(products, stripped);
    const counts = new Map();
    for (const p of subset) {
      let key;
      switch (dimension) {
        case 'baseShape': key = p.baseShape; break;
        case 'color':     key = p.color; break;
        case 'clarity':   key = p.clarity; break;
        case 'cutGrade':  key = p.cutGrade; break;
        case 'specialty': key = p.specialty; break;
        default: key = null;
      }
      if (key == null || key === '') continue;
      const k = String(key).toLowerCase();
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    return counts;
  }

  // ─── Public namespace ──────────────────────────────────────────
  SF.data = {
    classifyShape,
    loadAll,
    backgroundRefresh,
    applyFilters,
    facetCounts,
    STANDARD_SHAPES,
    _cache: { get: cacheGet, put: cachePut },
  };
  SF.api = {
    buildFilterURL,
    parseFilterURL,
  };
})(typeof window !== 'undefined' ? window : globalThis);
